'use client';

import { useState, useMemo } from 'react';
import Link from 'next/link';
import { NewsroomSidebar, MobileNewsroomBottomSheet } from './NewsroomSidebar';

const MIN_YEAR = 2024;

// w-10 (40px) on mobile → center at left-5 (20px)
// w-14 (56px) on md    → center at md:left-7 (28px)
const INDICATOR_COL = 'w-10 md:w-14';
const LINE_LEFT = 'left-5 md:left-7';

function groupByYear(items) {
    return items.reduce((acc, item) => {
        const year = (item._year ?? new Date(item.publishDate).getFullYear()).toString();
        if (!acc[year]) acc[year] = [];
        acc[year].push(item);
        return acc;
    }, {});
}

function getCategoryName(categories) {
    if (!Array.isArray(categories) || categories.length === 0) return null;
    const first = categories[0];
    if (first?.categoryName) return first.categoryName;
    const vals = Object.values(first);
    return vals[0] || null;
}

function TimelineItem({ item }) {
    const date = item.publishDate ? new Date(item.publishDate) : null;
    const month = date ? date.toLocaleDateString('en-US', { month: 'short' }) : '';
    const day = date ? date.getDate() : '';
    const category = getCategoryName(item.categories);

    return (
        <div className="group relative flex items-start py-3">
            {/* ── Indicator (outside the link) ── */}
            <div className={`relative z-10 flex shrink-0 justify-center pt-5 ${INDICATOR_COL}`}>
                {/* Ring 1 — blooms fast */}
                <span className="absolute size-3 scale-100 rounded-full bg-fuschia-700/0 transition-[transform,background-color] duration-300 ease-out group-hover:scale-[3.5] group-hover:bg-fuschia-700/15" />
                {/* Ring 2 — blooms slower, delayed */}
                <span className="group-hover:bg-fuschia-700/8 absolute size-3 scale-100 rounded-full bg-fuschia-700/0 transition-[transform,background-color] delay-75 duration-500 ease-out group-hover:scale-[6]" />
                {/* Dot shell */}
                <span className="relative z-10 block size-3 rounded-full border-2 border-blue-300 bg-white shadow-sm transition-all duration-200 group-hover:scale-110 group-hover:border-fuschia-700 group-hover:bg-fuschia-700 group-hover:shadow-md group-hover:shadow-fuschia-700/40" />
            </div>

            {/* ── Card = the actual link ── */}
            <Link
                href={`/news/${item.urlTitle}`}
                className="ml-3 flex-1 rounded-xl border p-4 pb-5 transition-all duration-300 group-hover:-translate-y-px group-hover:border-fuschia-600/20 group-hover:bg-white group-hover:shadow-lg group-hover:shadow-fuschia-600/10">
                <div className="mb-2 flex flex-wrap items-center gap-2">
                    {date && (
                        <span className="text-xs font-bold uppercase tracking-widest text-fuschia-700">
                            {month} {day}
                        </span>
                    )}
                    {category && (
                        <>
                            <span className="text-blue-300">·</span>
                            <span className="text-xs font-medium text-blue-500">{category}</span>
                        </>
                    )}
                </div>

                <div className="flex items-start justify-between gap-3">
                    <h3 className="text-lg line-clamp-2 font-bold leading-snug text-blue-700 transition-colors duration-200 group-hover:text-fuschia-700 md:text-xl">
                        {item.title}
                    </h3>
                    {/* Arrow — fades in and slides from left on hover */}
                    <span className="shrink-0 -translate-x-1 opacity-0 transition-all duration-200 group-hover:translate-x-0 group-hover:opacity-100">
                        <svg
                            className="mt-0.5 size-4 text-fuschia-700"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                    </span>
                </div>

                {item.teaser && (
                    <p className="mt-1.5 line-clamp-2 text-sm leading-relaxed text-blue-600/80">
                        {item.teaser}
                    </p>
                )}
            </Link>
        </div>
    );
}

export function NewsTimeline({ initialNews = [] }) {
    const [selectedYear, setSelectedYear] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');

    const recentNews = useMemo(
        () =>
            initialNews.reduce((acc, item) => {
                if (!item.publishDate) return acc;
                const year = new Date(item.publishDate).getFullYear();
                if (year >= MIN_YEAR) acc.push({ ...item, _year: year });
                return acc;
            }, []),
        [initialNews]
    );

    const years = useMemo(
        () => Array.from(new Set(recentNews.map((item) => item._year))).sort((a, b) => b - a),
        [recentNews]
    );

    const filteredNews = useMemo(() => {
        const q = searchQuery.toLowerCase();
        return recentNews.filter((item) => {
            const matchesYear = selectedYear === 'all' || item._year === Number(selectedYear);
            const matchesSearch =
                !q ||
                item.title?.toLowerCase().includes(q) ||
                item.teaser?.toLowerCase().includes(q);
            return matchesYear && matchesSearch;
        });
    }, [recentNews, selectedYear, searchQuery]);

    const groupedByYear = useMemo(() => groupByYear(filteredNews), [filteredNews]);
    const sortedYears = Object.keys(groupedByYear).sort((a, b) => Number(b) - Number(a));

    return (
        <div className="container relative mx-auto px-4 py-16 lg:py-24">
            <div className="lg:flex lg:items-start lg:gap-4 xl:gap-8">
                {/* ── Left: timeline ── */}
                <div className="min-h-[800px] min-w-0 flex-1">
                    {/* Header */}
                    <div className="mb-12">
                        <p className="mb-3 text-xs font-bold uppercase tracking-widest text-fuschia-700">
                            Newsroom
                        </p>
                        <h1 className="md:text-6xl mb-4 text-5xl font-bold text-blue-700">
                            dotCMS News
                        </h1>
                        <p className="max-w-xl text-xl text-blue-600">
                            Stay up to date with the latest news, press releases, and announcements
                            from dotCMS.
                        </p>
                    </div>

                    {/* Filters */}
                    <div className="mb-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex flex-wrap gap-2">
                            <button
                                onClick={() => setSelectedYear('all')}
                                className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors duration-200 ${
                                    selectedYear === 'all'
                                        ? 'bg-fuschia-700 text-white'
                                        : 'bg-blue-100 text-blue-600 hover:bg-blue-200 hover:text-blue-700'
                                }`}>
                                All
                            </button>
                            {years.map((year) => (
                                <button
                                    key={year}
                                    onClick={() =>
                                        setSelectedYear(
                                            year.toString() === selectedYear.toString()
                                                ? 'all'
                                                : year
                                        )
                                    }
                                    className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors duration-200 ${
                                        selectedYear.toString() === year.toString()
                                            ? 'bg-fuschia-700 text-white'
                                            : 'bg-blue-100 text-blue-600 hover:bg-blue-200 hover:text-blue-700'
                                    }`}>
                                    {year}
                                </button>
                            ))}
                        </div>

                        <div className="relative w-full sm:w-auto sm:max-w-xs">
                            <input
                                type="text"
                                placeholder="Search news…"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full rounded-full border border-blue-200 bg-white py-1.5 pl-9 pr-4 text-sm text-blue-700 placeholder:text-blue-400 focus:border-fuschia-700 focus:outline-none focus:ring-2 focus:ring-fuschia-700/20"
                            />
                            <svg
                                className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-blue-400"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                />
                            </svg>
                        </div>
                    </div>

                    {filteredNews.length === 0 && (
                        <div className="py-16 text-center">
                            <p className="text-blue-500">No news found matching your search.</p>
                        </div>
                    )}

                    {/* Timeline */}
                    {sortedYears.map((year) => {
                        const items = groupedByYear[year];
                        return (
                            <div key={year} className="mb-14">
                                {/* Year header — shares the same left offset as indicator column */}
                                <div className="mb-2 flex items-center gap-3">
                                    <div className={`shrink-0 ${INDICATOR_COL}`}>
                                        <span className="block rounded-full bg-blue-100 px-2 py-0.5 text-center text-xs font-bold uppercase tracking-widest text-blue-600">
                                            {year}
                                        </span>
                                    </div>
                                    <div className="h-px flex-1 bg-blue-200" />
                                    <span className="text-xs font-medium text-blue-400">
                                        {items.length} {items.length === 1 ? 'item' : 'items'}
                                    </span>
                                </div>

                                {/* Items container with vertical line */}
                                <div className="relative">
                                    {/* Vertical line — top-5 aligns with first dot center (py-3 + pt-5 + half dot) */}
                                    <div
                                        className={`absolute inset-y-5 w-px bg-blue-200 ${LINE_LEFT}`}
                                    />
                                    {items.map((item) => (
                                        <TimelineItem key={item.identifier} item={item} />
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
                {/* end left column */}

                {/* ── Right: placeholder <aside> reserves layout space; sidebar is self-fixed ── */}
                <aside className="hidden w-[26rem] shrink-0 lg:block">
                    <NewsroomSidebar />
                </aside>
            </div>
            {/* end flex row */}

            <MobileNewsroomBottomSheet />
        </div>
    );
}
